import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import {
  ConsentLoading,
  ConsentScreen,
  ConsentUnavailable,
} from "@/components/agents/consent-screen";
import { useTRPC } from "@/utils/trpc";

const searchSchema = z.object({
  request: z.uuid().optional().catch(undefined),
});

/**
 * `/authorize?request=<id>` — where the MCP server sends a person to decide
 * whether an AI app may use their PostDaily account. The id names a pending
 * request the MCP worker recorded; approving or denying redirects back to it.
 */
export const Route = createFileRoute("/(connect)/authorize")({
  validateSearch: (search) => searchSchema.parse(search),
  component: RouteComponent,
});

function RouteComponent() {
  const { request: requestId } = Route.useSearch();
  const trpc = useTRPC();
  const request = useQuery(
    trpc.agents.getAuthorization.queryOptions(
      { requestId: requestId ?? "" },
      {
        enabled: Boolean(requestId),
        retry: false,
        staleTime: Number.POSITIVE_INFINITY,
      },
    ),
  );

  if (!requestId) {
    return (
      <ConsentUnavailable message="This page needs a connection request from your AI app." />
    );
  }

  if (request.isPending) {
    return <ConsentLoading />;
  }

  if (request.error) {
    return <ConsentUnavailable message={request.error.message} />;
  }

  return <ConsentScreen request={request.data} />;
}
